/**
 * TEMP-H-019: Stage Color Normalization System
 * 
 * Centralized stage→semantic token mapping for consistent theming
 * across all pipeline visualizations. Replaces per-component hardcoded color palettes.
 */

export type StageType = 'script' | 'vision' | 'storyboard' | 'shots' | 'continuity' | 'frames' | 'videos' | 'elements' | 'qc' | 'assemble' | 'music' | 'publish' | 'music_master' | 'music_sidechain' | 'export_preview';

export interface StageColorConfig {
  name: string;
  // CSS custom property references for theming
  primaryColor: string;      // Main stage color (--accent, --success, etc.)
  bgSubtle: string;         // Background tint
  borderSubtle: string;     // Border color
  textColor: string;        // Text on stage color
  glowColor: string;        // Glow effects
}

/**
 * Stage color mapping using semantic tokens
 * Maps each stage to consistent semantic colors that work across themes
 */
export const STAGE_COLORS: Record<StageType, StageColorConfig> = {
  script: {
    name: 'Script',
    primaryColor: 'var(--accent)',
    bgSubtle: 'var(--accent-bg-subtle)',
    borderSubtle: 'var(--accent-border-subtle)',
    textColor: 'var(--accent)',
    glowColor: 'var(--accent)'
  },
  vision: {
    name: 'Vision', 
    primaryColor: 'var(--info)',
    bgSubtle: 'var(--info-bg-subtle)',
    borderSubtle: 'var(--info-border-subtle)',
    textColor: 'var(--info)',
    glowColor: 'var(--info)'
  },
  storyboard: {
    name: 'Storyboard',
    primaryColor: 'var(--success)',
    bgSubtle: 'var(--success-bg-subtle)',
    borderSubtle: 'var(--success-border-subtle)', 
    textColor: 'var(--success)',
    glowColor: 'var(--success)'
  },
  shots: {
    name: 'Shots',
    primaryColor: 'var(--warning)',
    bgSubtle: 'var(--warning-bg-subtle)',
    borderSubtle: 'var(--warning-border-subtle)',
    textColor: 'var(--warning)',
    glowColor: 'var(--warning)'
  },
  continuity: {
    name: 'Continuity', 
    primaryColor: 'var(--danger)',
    bgSubtle: 'var(--danger-bg-subtle)',
    borderSubtle: 'var(--danger-border-subtle)',
    textColor: 'var(--danger)',
    glowColor: 'var(--danger)'
  },
  frames: {
    name: 'Frames',
    primaryColor: 'var(--effect-purple)',
    bgSubtle: 'color-mix(in srgb, var(--effect-purple) 10%, var(--bg-surface))',
    borderSubtle: 'color-mix(in srgb, var(--effect-purple) 30%, var(--border-default))',
    textColor: 'var(--effect-purple)',
    glowColor: 'var(--effect-purple-glow)'
  },
  videos: {
    name: 'Videos',
    primaryColor: 'var(--effect-cyan)',
    bgSubtle: 'color-mix(in srgb, var(--effect-cyan) 10%, var(--bg-surface))',
    borderSubtle: 'color-mix(in srgb, var(--effect-cyan) 30%, var(--border-default))',
    textColor: 'var(--effect-cyan)',
    glowColor: 'var(--effect-cyan-glow)'
  },
  music: {
    name: 'Music',
    primaryColor: 'var(--effect-magenta)',
    bgSubtle: 'color-mix(in srgb, var(--effect-magenta) 10%, var(--bg-surface))',
    borderSubtle: 'color-mix(in srgb, var(--effect-magenta) 30%, var(--border-default))',
    textColor: 'var(--effect-magenta)',
    glowColor: 'var(--effect-magenta-glow)'
  },
  elements: {
    name: 'Elements',
    primaryColor: 'var(--effect-orange)',
    bgSubtle: 'color-mix(in srgb, var(--effect-orange) 10%, var(--bg-surface))',
    borderSubtle: 'color-mix(in srgb, var(--effect-orange) 30%, var(--border-default))',
    textColor: 'var(--effect-orange)',
    glowColor: 'var(--effect-orange-glow)'
  },
  qc: {
    name: 'Quality Control',
    primaryColor: 'var(--warning)',
    bgSubtle: 'var(--warning-bg-subtle)', 
    borderSubtle: 'var(--warning-border-subtle)',
    textColor: 'var(--warning)',
    glowColor: 'var(--warning)'
  },
  assemble: {
    name: 'Assemble',
    primaryColor: 'var(--effect-teal)',
    bgSubtle: 'color-mix(in srgb, var(--effect-teal) 10%, var(--bg-surface))',
    borderSubtle: 'color-mix(in srgb, var(--effect-teal) 30%, var(--border-default))',
    textColor: 'var(--effect-teal)',
    glowColor: 'var(--effect-teal-glow)'
  },
  publish: {
    name: 'Publish',
    primaryColor: 'var(--success)', 
    bgSubtle: 'var(--success-bg-subtle)',
    borderSubtle: 'var(--success-border-subtle)',
    textColor: 'var(--success)',
    glowColor: 'var(--success)'
  },
  music_master: {
    name: 'Music Master',
    primaryColor: 'var(--effect-indigo)',
    bgSubtle: 'color-mix(in srgb, var(--effect-indigo) 10%, var(--bg-surface))',
    borderSubtle: 'color-mix(in srgb, var(--effect-indigo) 30%, var(--border-default))',
    textColor: 'var(--effect-indigo)',
    glowColor: 'var(--effect-indigo-glow)'
  },
  music_sidechain: {
    name: 'Music Sidechain',
    primaryColor: 'var(--effect-pink)',
    bgSubtle: 'color-mix(in srgb, var(--effect-pink) 10%, var(--bg-surface))',
    borderSubtle: 'color-mix(in srgb, var(--effect-pink) 30%, var(--border-default))',
    textColor: 'var(--effect-pink)',
    glowColor: 'var(--effect-pink-glow)'
  },
  export_preview: {
    name: 'Export Preview',
    primaryColor: 'var(--info)',
    bgSubtle: 'var(--info-bg-subtle)',
    borderSubtle: 'var(--info-border-subtle)',
    textColor: 'var(--info)',
    glowColor: 'var(--info)'
  }
};

/**
 * Get stage color configuration
 */
export const getStageColors = (stage: StageType): StageColorConfig => {
  return STAGE_COLORS[stage] || STAGE_COLORS.script;
};

/**
 * CSS class generator for stage styling
 */
export const getStageClasses = (stage: StageType) => {
  const config = getStageColors(stage);
  return {
    // For use in style props
    background: config.bgSubtle,
    border: `1px solid ${config.borderSubtle}`,
    color: config.textColor,
    
    // CSS custom properties for component use
    '--stage-primary': config.primaryColor,
    '--stage-bg': config.bgSubtle,
    '--stage-border': config.borderSubtle,
    '--stage-text': config.textColor,
    '--stage-glow': config.glowColor
  } as React.CSSProperties;
};

/**
 * Audio type color mapping for consistency across music components
 */
export const AUDIO_TYPE_COLORS = {
  master: {
    bg: 'var(--effect-blue)',
    bgSubtle: 'color-mix(in srgb, var(--effect-blue) 10%, var(--bg-surface))',
    text: 'var(--effect-blue)'
  },
  mastered: {
    bg: 'var(--effect-purple)', 
    bgSubtle: 'color-mix(in srgb, var(--effect-purple) 10%, var(--bg-surface))',
    text: 'var(--effect-purple)'
  },
  sidechained: {
    bg: 'var(--success)',
    bgSubtle: 'var(--success-bg-subtle)',
    text: 'var(--success)'
  },
  stems: {
    bg: 'var(--warning)',
    bgSubtle: 'var(--warning-bg-subtle)', 
    text: 'var(--warning)'
  },
  preview: {
    bg: 'var(--effect-cyan)',
    bgSubtle: 'color-mix(in srgb, var(--effect-cyan) 10%, var(--bg-surface))',
    text: 'var(--effect-cyan)'
  },
  default: {
    bg: 'var(--fg-muted)',
    bgSubtle: 'var(--bg-subtle)',
    text: 'var(--fg-muted)'
  }
};

/**
 * Get audio type colors
 */
export const getAudioTypeColors = (audioType?: string) => {
  return AUDIO_TYPE_COLORS[audioType as keyof typeof AUDIO_TYPE_COLORS] || AUDIO_TYPE_COLORS.default;
};